import { ACLPermissionEnum } from '../app/constants'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  UserSessionResponse,
} from '../app/interfaces'
import type { FileDetailsResponse } from '../app/interfaces/FileDetailsResponse'
import { isRestrictedUser } from './roles'

export const checkPermission = ({
  user,
  permission,
}: {
  user: UserSessionResponse | null
  permission: PermissionsEnum
}): boolean => user?.permissions.split(',').includes(permission) ?? false

export const checkOperationPermission = ({
  user,
  aclPermission,
  selectedFiles,
  selectedFolders,
  selectedMediaCollections,
}: {
  user: UserSessionResponse | null
  aclPermission: ACLPermissionEnum[]
  selectedFiles: MediaLibrarySearchFileResponse[] | FileDetailsResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
}): boolean => {
  if (!(user?.roles && isRestrictedUser(user.roles))) {
    return true
  }

  return ![
    ...selectedFiles,
    ...selectedFolders,
    ...selectedMediaCollections,
  ].filter(file => file.permission && !aclPermission.includes(file.permission))
    .length
}

export const checkOperationDisabled = ({
  user,
  aclPermission,
  selectedFiles,
  selectedFolders,
  selectedMediaCollections,
}: {
  user: UserSessionResponse | null
  aclPermission: ACLPermissionEnum[]
  selectedFiles: MediaLibrarySearchFileResponse[] | FileDetailsResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
}): { isDisabled: boolean; remainingLength?: Number; assetName?: String } => {
  if (!(user?.roles && isRestrictedUser(user.roles))) {
    return { isDisabled: false, remainingLength: -1, assetName: '' }
  }

  const disableArray = [
    ...selectedFiles,
    ...selectedFolders,
    ...selectedMediaCollections,
  ]
    .filter(file => file.permission && aclPermission.includes(file.permission))
    .map(x => x.name)

  return {
    isDisabled: disableArray.length > 0,
    remainingLength: disableArray.length - 1,
    assetName: disableArray.length > 0 ? disableArray[0] : '',
  }
}

export enum PermissionsEnum {
  // Imagekit permissions
  ADMIN_PANEL_VIEW = 'ADMIN_PANEL_VIEW',
  ADMIN_PANEL_MANAGE = 'ADMIN_PANEL_MANAGE',
  CLIENT_VIEW = 'CLIENT_VIEW',
  CLIENT_MANAGE = 'CLIENT_MANAGE',
  USER_VIEW = 'USER_VIEW',
  USER_MANAGE = 'USER_MANAGE',
  // Client permissions
  SELF_ANALYTICS_VIEW = 'SELF_ANALYTICS_VIEW',
  SELF_ANALYTICS_MANAGE = 'SELF_ANALYTICS_MANAGE',
  SELF_BILLING_VIEW = 'SELF_BILLING_VIEW',
  SELF_BILLING_MANAGE = 'SELF_BILLING_MANAGE',
  SELF_CLEAR_CACHE_VIEW = 'SELF_CLEAR_CACHE_VIEW',
  SELF_CLEAR_CACHE_MANAGE = 'SELF_CLEAR_CACHE_MANAGE',
  SELF_CLIENT_VIEW = 'SELF_CLIENT_VIEW',
  SELF_CLIENT_MANAGE = 'SELF_CLIENT_MANAGE',
  SELF_DEVELOPERS_VIEW = 'SELF_DEVELOPERS_VIEW',
  SELF_DEVELOPERS_MANAGE = 'SELF_DEVELOPERS_MANAGE',
  SELF_IMAGE_SETTINGS_VIEW = 'SELF_IMAGE_SETTINGS_VIEW',
  SELF_IMAGE_SETTINGS_MANAGE = 'SELF_IMAGE_SETTINGS_MANAGE',
  SELF_INTEGRATION_VIEW = 'SELF_INTEGRATION_VIEW',
  SELF_INTEGRATION_MANAGE = 'SELF_INTEGRATION_MANAGE',
  RESTRICTED_SELF_MEDIA_LIBRARY_VIEW = 'RESTRICTED_SELF_MEDIA_LIBRARY_VIEW',
  RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE = 'RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE',
  SELF_MEDIA_LIBRARY_VIEW = 'SELF_MEDIA_LIBRARY_VIEW',
  SELF_MEDIA_LIBRARY_MANAGE = 'SELF_MEDIA_LIBRARY_MANAGE',
  SELF_PERFORMANCE_VIEW = 'SELF_PERFORMANCE_VIEW',
  SELF_PERFORMANCE_MANAGE = 'SELF_PERFORMANCE_MANAGE',
  SELF_TEAM_VIEW = 'SELF_TEAM_VIEW',
  SELF_TEAM_MANAGE = 'SELF_TEAM_MANAGE',
  SELF_REFERRAL_VIEW = 'SELF_REFERRAL_VIEW',
  SELF_REFERRAL_MANAGE = 'SELF_REFERRAL_MANAGE',
  SELF_USAGE_VIEW = 'SELF_USAGE_VIEW',
  SELF_USAGE_MANAGE = 'SELF_USAGE_MANAGE',
  SELF_USER_VIEW = 'SELF_USER_VIEW',
  SELF_USER_MANAGE = 'SELF_USER_MANAGE',
  // Agency only permissions
  SELF_AGENCY_ANALYTICS_VIEW = 'SELF_AGENCY_ANALYTICS_VIEW',
  SELF_AGENCY_ANALYTICS_MANAGE = 'SELF_AGENCY_ANALYTICS_MANAGE',
  SELF_CHILD_VIEW = 'SELF_CHILD_VIEW',
  SELF_CHILD_MANAGE = 'SELF_CHILD_MANAGE',
}

export const PermissionDescriptionMap = {
  [ACLPermissionEnum.MANAGE]: {
    label: 'Can manage',
    description_folder:
      'Full asset Management, including deleting, moving and sharing',
    description_file:
      'Full asset Management, including deleting, moving and sharing',
    description_collection:
      'Full asset Management including deleting and sharing',
    value: ACLPermissionEnum.MANAGE,
  },
  [ACLPermissionEnum.CONTRIBUTE]: {
    label: 'Can contribute',
    description_folder:
      'View, download, add and edit assets, and create sub-folders',
    description_file: 'View, download and edit file',
    description_collection: 'View, download and edit assets',
    value: ACLPermissionEnum.CONTRIBUTE,
  },
  [ACLPermissionEnum.READ]: {
    label: 'Can view',
    description_folder: 'View and download assets',
    description_file: 'View and download file',
    description_collection: 'View and download assets',
    value: ACLPermissionEnum.READ,
  },
}

export const aclPermissionOrderMapping = {
  [ACLPermissionEnum.READ]: 1,
  [ACLPermissionEnum.CONTRIBUTE]: 2,
  [ACLPermissionEnum.MANAGE]: 3,
}
